import type { BadgeProps } from '@/ui';
import { formatLedgerAmount } from './transactions';

/** Ported from `Enums.BonusTypes`. */
export const BonusType = {
  MoneyBack: 0,
  AttractiveCombi: 1,
  MoneyBackLive: 2,
  MoneyBackCombo: 3,
  WelcomeBonus100: 4,
  WelcomeBonus150: 5,
  FreeBet: 6,
  BackupBet: 7,
  CasinoBonus: 8,
  RegisterBonus: 9,
  FreeCashback: 10,
  LostBonus: 11,
} as const;

export type BonusType = (typeof BonusType)[keyof typeof BonusType];

export const BONUS_TYPE_LABEL: Record<BonusType, string> = {
  [BonusType.MoneyBack]: 'Money back',
  [BonusType.AttractiveCombi]: 'Combi bonus',
  [BonusType.MoneyBackLive]: 'Live money back',
  [BonusType.MoneyBackCombo]: 'Combo money back',
  [BonusType.WelcomeBonus100]: 'Welcome bonus 100%',
  [BonusType.WelcomeBonus150]: 'Welcome bonus 150%',
  [BonusType.FreeBet]: 'Free bet',
  [BonusType.BackupBet]: 'Backup bet',
  [BonusType.CasinoBonus]: 'Casino bonus',
  [BonusType.RegisterBonus]: 'Registration bonus',
  [BonusType.FreeCashback]: 'Free cashback',
  [BonusType.LostBonus]: 'Lost bonus',
};

/**
 * The first four types are the ones the betslip can apply to a selection set, and
 * the legacy code addressed them by the names in `Mappings.BonusNames`.
 */
export const BETSLIP_BONUS_TYPES = [
  BonusType.MoneyBack,
  BonusType.AttractiveCombi,
  BonusType.MoneyBackLive,
  BonusType.MoneyBackCombo,
] as const;

export const BonusStatus = {
  Pending: 0,
  Qualified: 1,
  Active: 2,
  Declined: 3,
  Completed: 4,
  Expired: 5,
  Cancelled: 6,
} as const;

export type BonusStatus = (typeof BonusStatus)[keyof typeof BonusStatus];

export const BONUS_STATUS_LABEL: Record<BonusStatus, string> = {
  [BonusStatus.Pending]: 'Pending',
  [BonusStatus.Qualified]: 'Qualified',
  [BonusStatus.Active]: 'Active',
  [BonusStatus.Declined]: 'Declined',
  [BonusStatus.Completed]: 'Completed',
  [BonusStatus.Expired]: 'Expired',
  [BonusStatus.Cancelled]: 'Cancelled',
};

type Tone = NonNullable<BadgeProps['tone']>;

export const BONUS_STATUS_TONE: Record<BonusStatus, Tone> = {
  [BonusStatus.Pending]: 'warning',
  [BonusStatus.Qualified]: 'info',
  [BonusStatus.Active]: 'info',
  [BonusStatus.Declined]: 'danger',
  [BonusStatus.Completed]: 'success',
  [BonusStatus.Expired]: 'danger',
  [BonusStatus.Cancelled]: 'danger',
};

/** Ported from `Enums.BonusesModules`. */
export const BonusModule = {
  All: 0,
  Sportsbook: 1,
  Casino: 2,
  ExternalSportsbook: 3,
} as const;

export type BonusModule = (typeof BonusModule)[keyof typeof BonusModule];

/**
 * UsersAPI `DepositBonusSettingsMessage` — player-socket `DepositBonusSettings`
 * and lost-bonus claim response. Stored on the session as SportsbookBonus /
 * CasinoBonus (legacy `UpdateBonusSettings`).
 */
export interface DepositBonusSettings {
  Type?: number | null;
  Name?: string | null;
  Status?: number | null;
  MinimumTicketQuota?: number | null;
  MinimumEventQuota?: number | null;
  GlobalBets?: number | null;
  DepositBonusCredits?: number | null;
  Vendors?: string[] | null;
  GameCategory?: string | null;
}

export interface DepositBonusSettingsPatch {
  SportsbookBonus?: DepositBonusSettings | null;
  CasinoBonus?: DepositBonusSettings | null;
  /** True when Status is Active and a module bonus was set (show toast). */
  isNewActive: boolean;
}

/**
 * Apply a deposit-bonus settings message the way legacy `UpdateBonusSettings` does.
 * Returns null when Type is not Sportsbook/Casino.
 */
export function depositBonusSettingsPatch(
  message: DepositBonusSettings | null | undefined,
): DepositBonusSettingsPatch | null {
  if (!message || message.Type == null) return null;
  const type = Number(message.Type);
  if (type !== BonusModule.Sportsbook && type !== BonusModule.Casino) return null;

  const status = Number(message.Status);
  if (status === BonusStatus.Active) {
    const { Status: _status, DepositBonusCredits: _credits, ...rest } = message;
    const stored: DepositBonusSettings = { ...rest, Type: type };
    return type === BonusModule.Sportsbook
      ? { SportsbookBonus: stored, isNewActive: true }
      : { CasinoBonus: stored, isNewActive: true };
  }

  return type === BonusModule.Sportsbook
    ? { SportsbookBonus: null, isNewActive: false }
    : { CasinoBonus: null, isNewActive: false };
}

/** List primary filter — legacy Active vs Offers History. */
export type BonusBucket = 'active' | 'history';

/** List sub-filter by product module. */
export type BonusModuleFilter = 'all' | 'sport' | 'casino';

/** UsersAPI `BonusTransactionsResponse`. */
export interface BonusTransaction {
  ModuleId: number;
  WagerType: number;
  Id: string;
  Name: string;
  Amount: number;
  TargetAmount?: number | null;
  UsedAmount?: number | null;
  CasinoBonusCredits?: number | null;
  SportsbookBonusCredits?: number | null;
  ExternalSportsbookBonusCredits?: number | null;
  Date?: string | null;
  Status: number;
  Progress: number;
  Note?: string | null;
  Completing?: string | null;
  Type: number;
  StartDate?: string | null;
  ExpiryDate?: string | null;
  DateSpan?: number | null;
  Timestamp?: string;
}

/** Related ticket stub on bonus details. */
export interface BonusTransactionTicket {
  Id?: string | number | null;
  TicketId?: string | number | null;
  [key: string]: unknown;
}

/** UsersAPI `BonusTransactionsResponseDetails`. */
export interface BonusTransactionDetails extends BonusTransaction {
  NumberOfTickets?: number | null;
  MinimumBetsTicket?: number | null;
  MinimumTicketQuota?: number | null;
  MaximumTicketQuota?: number | null;
  MinimumEventQuota?: number | null;
  DepositRequiredCompleted?: boolean;
  Tickets?: BonusTransactionTicket[] | null;
  Ticket?: BonusTransactionTicket | null;
  MachineId?: number;
  WalletId?: string | null;
  InGameBonusGames?: Array<{
    Tag?: string | null;
    Name?: string | null;
    Vendor?: string | null;
    Date?: string | null;
  }> | null;
  GameCategory?: string | null;
  Vendors?: string[] | null;
}

export interface BonusTransactionsListRequest {
  Value: string;
}

export interface BonusTransactionDetailsRequest {
  Value: string;
  Type: number;
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** FreeBet / BackupBet — no wagering progress bar. */
export function isRewardBonusType(type: number): boolean {
  return type === BonusType.FreeBet || type === BonusType.BackupBet;
}

export function isBonusInActiveBucket(
  bonus: Pick<BonusTransaction, 'Status' | 'Type' | 'ExpiryDate'>,
  now: Date = new Date(),
): boolean {
  if (bonus.Status !== BonusStatus.Active) return false;
  if (!isRewardBonusType(bonus.Type)) return true;
  if (!bonus.ExpiryDate) return true;
  return new Date(bonus.ExpiryDate) >= now;
}

export function filterBonusesByBucket(
  bonuses: BonusTransaction[],
  bucket: BonusBucket,
  now: Date = new Date(),
): BonusTransaction[] {
  return bonuses.filter((bonus) => {
    const active = isBonusInActiveBucket(bonus, now);
    return bucket === 'active' ? active : !active;
  });
}

export function matchesBonusModuleFilter(
  moduleId: number,
  filter: BonusModuleFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'sport') {
    return (
      moduleId === BonusModule.Sportsbook ||
      moduleId === BonusModule.ExternalSportsbook
    );
  }
  return moduleId === BonusModule.Casino;
}

export function filterBonusesByModule(
  bonuses: BonusTransaction[],
  filter: BonusModuleFilter,
): BonusTransaction[] {
  if (filter === 'all') return bonuses;
  return bonuses.filter((bonus) => matchesBonusModuleFilter(bonus.ModuleId, filter));
}

export function filterBonuses(
  bonuses: BonusTransaction[],
  bucket: BonusBucket,
  module: BonusModuleFilter,
  now: Date = new Date(),
): BonusTransaction[] {
  return filterBonusesByModule(filterBonusesByBucket(bonuses, bucket, now), module);
}

export function bonusModuleLabel(moduleId: number): string {
  if (moduleId === BonusModule.Casino) return 'Casino';
  if (moduleId === BonusModule.ExternalSportsbook) return 'Sports';
  if (moduleId === BonusModule.Sportsbook) return 'Sports';
  return 'Sports';
}

export function bonusKindLabel(type: number): string {
  return isRewardBonusType(type) ? 'Reward' : 'Wagering';
}

/** List subtitle: `Sports · Wagering`. */
export function bonusListSubtitle(bonus: Pick<BonusTransaction, 'ModuleId' | 'Type'>): string {
  return `${bonusModuleLabel(bonus.ModuleId)} · ${bonusKindLabel(bonus.Type)}`;
}

/** Details type line: `Sports bonus` / `Casino bonus`. */
export function bonusDetailsTypeLabel(moduleId: number): string {
  return moduleId === BonusModule.Casino ? 'Casino bonus' : 'Sports bonus';
}

/**
 * Display status for list/details badges.
 * Active past expiry → Expired; FreeBet/BackupBet non-active → Used.
 */
export function bonusDisplayStatus(
  bonus: Pick<BonusTransaction, 'Status' | 'Type' | 'ExpiryDate'>,
  now: Date = new Date(),
): string {
  if (bonus.Status === BonusStatus.Cancelled) return 'Canceled';
  if (
    bonus.Status === BonusStatus.Active &&
    bonus.ExpiryDate &&
    new Date(bonus.ExpiryDate) <= now
  ) {
    return 'Expired';
  }
  if (isRewardBonusType(bonus.Type) && bonus.Status !== BonusStatus.Active) {
    return 'Used';
  }
  return BONUS_STATUS_LABEL[bonus.Status as BonusStatus] ?? 'Unknown';
}

/** Badge tone for the displayed status string (Figma-aligned). */
export function bonusDisplayStatusTone(statusLabel: string): Tone {
  switch (statusLabel) {
    case 'Active':
      return 'success';
    case 'Qualified':
    case 'Completed':
      return 'success';
    case 'Used':
    case 'Pending':
      return 'warning';
    case 'Expired':
    case 'Canceled':
    case 'Cancelled':
    case 'Declined':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function formatBonusDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS_SHORT[date.getMonth()] ?? '';
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export function formatBonusDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/** `01 Oct 2026 – 31 Oct 2026` */
export function formatBonusDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const startLabel = formatBonusDate(start);
  const endLabel = formatBonusDate(end);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel;
}

export function formatBonusAmount(value: number, currency = ''): string {
  return formatLedgerAmount(value, currency);
}

export function bonusProgressPercent(bonus: Pick<BonusTransaction, 'Progress' | 'Type'>): number {
  if (isRewardBonusType(bonus.Type)) return 0;
  const value = Number(bonus.Progress);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function bonusProgressAmountsLabel(
  bonus: Pick<BonusTransaction, 'UsedAmount' | 'TargetAmount' | 'Amount' | 'Type'>,
  currency = '',
): string {
  if (isRewardBonusType(bonus.Type)) return '';
  const used = bonus.UsedAmount ?? 0;
  const target = bonus.TargetAmount ?? bonus.Amount ?? 0;
  return `${formatBonusAmount(used, currency)} / ${formatBonusAmount(target, currency)}`;
}

/** Countdown like `Expires in 5d 12h`; empty when already expired or no date. */
export function bonusExpiryCountdown(
  expiryDate: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!expiryDate) return '';
  const end = new Date(expiryDate);
  if (Number.isNaN(end.getTime())) return '';
  const ms = end.getTime() - now.getTime();
  if (ms <= 0) return '';
  const totalHours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0) return `Expires in ${days}d ${hours}h`;
  if (hours > 0) return `Expires in ${hours}h`;
  const minutes = Math.max(1, Math.floor(ms / (1000 * 60)));
  return `Expires in ${minutes}m`;
}

export type BonusDetailsCta =
  | { kind: 'continueBetting'; label: string }
  | { kind: 'useFreeBet'; label: string }
  | { kind: 'viewTicket'; label: string; ticketId: string }
  | null;

export function bonusDetailsCta(
  details: Pick<BonusTransactionDetails, 'Status' | 'Type' | 'ExpiryDate' | 'Ticket' | 'Tickets'>,
  now: Date = new Date(),
): BonusDetailsCta {
  const display = bonusDisplayStatus(details, now);
  const ticketId = firstBonusTicketId(details);

  if (isRewardBonusType(details.Type)) {
    if (display === 'Active' && details.Type === BonusType.FreeBet) {
      return { kind: 'useFreeBet', label: 'Use free bet' };
    }
    if (display === 'Used' && ticketId) {
      return { kind: 'viewTicket', label: 'View ticket', ticketId };
    }
    return null;
  }

  if (display === 'Active') {
    return { kind: 'continueBetting', label: 'Continue betting' };
  }
  return null;
}

function firstBonusTicketId(
  details: Pick<BonusTransactionDetails, 'Ticket' | 'Tickets'>,
): string | null {
  const fromSingle = details.Ticket?.Id ?? details.Ticket?.TicketId;
  if (fromSingle != null && String(fromSingle).trim()) return String(fromSingle);
  const first = details.Tickets?.[0];
  const fromList = first?.Id ?? first?.TicketId;
  if (fromList != null && String(fromList).trim()) return String(fromList);
  return null;
}

export interface BonusTermsMetaRow {
  label: string;
  value: string;
}

/** Terms meta rows for details; reward kinds get the short set. */
export function bonusTermsMetaRows(
  details: BonusTransactionDetails,
  currency = '',
): BonusTermsMetaRow[] {
  if (isRewardBonusType(details.Type)) {
    return compactMeta([
      { label: 'Amount', value: formatBonusAmount(details.Amount, currency) },
      {
        label: 'Transaction Date',
        value: formatBonusDateTime(details.Date ?? details.StartDate),
      },
      {
        label: 'Max Ticket Odds',
        value: formatOptionalNumber(details.MaximumTicketQuota),
      },
      {
        label: 'Min Ticket Odds',
        value: formatOptionalNumber(details.MinimumTicketQuota),
      },
    ]);
  }

  return compactMeta([
    {
      label: 'Target Amount',
      value:
        details.TargetAmount != null
          ? formatBonusAmount(details.TargetAmount, currency)
          : '',
    },
    { label: 'Amount', value: formatBonusAmount(details.Amount, currency) },
    {
      label: 'Played Amount',
      value:
        details.UsedAmount != null
          ? formatBonusAmount(details.UsedAmount, currency)
          : '',
    },
    {
      label: 'Transaction Date',
      value: formatBonusDateTime(details.Date ?? details.StartDate),
    },
    {
      label: 'Minimum bets in ticket',
      value: formatOptionalNumber(details.MinimumBetsTicket),
    },
    {
      label: 'Minimum event odds',
      value: formatOptionalNumber(details.MinimumEventQuota),
    },
    {
      label: 'Min Ticket Odds',
      value: formatOptionalNumber(details.MinimumTicketQuota),
    },
    {
      label: 'Max Ticket Odds',
      value: formatOptionalNumber(details.MaximumTicketQuota),
    },
  ]);
}

function formatOptionalNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return String(value);
}

function compactMeta(rows: BonusTermsMetaRow[]): BonusTermsMetaRow[] {
  return rows.filter((row) => row.value.trim().length > 0);
}
